const contentful = require('contentful');
const slugify = require('slugify');

const typePaths = {
  'news': 'news',
  'contentPage': '',
  'festival': '',
  'event': 'events',
  'form': 'forms'
};

const slugOptions = {
  remove: /[*+~.()-/?#'",!:@]/g,
  lower: true
}

const languages = ['nl', 'fr', 'en'];

const __clone = entry => JSON.parse(JSON.stringify(entry));

class ContentfulEngine {
  constructor ({ space, production, preview, previewMode = false }) {
    const environment = 'version-2';
    this.production = contentful.createClient({ space, accessToken: production, environment });
    this.preview = contentful.createClient({ space, accessToken: preview, environment, host: 'preview.contentful.com' });
    this.mode = previewMode ? 'preview' : 'production';
    this.shared = {};
  }

  get client () {
    return this.mode === 'preview' ? this.preview : this.production;
  }

  refreshShared () {

  }

  static makeSlugs (entry) {
    if (entry.slug) return entry;
    if (!(entry.type && typePaths.hasOwnProperty(entry.type))) return entry;
    
    languages.forEach(language => {
      let title
        =  entry[`${language}ShortTitle`]
        || entry[`${language}Title`]
        || entry[`${language}LongTitle`]
        || 'missing-title';
        
      let festival = entry.festival
        ? entry.festival[`${language}Title`]
        : '';

      let path = typePaths[entry.type];

      entry[`${language}Slug`] = '/' + [
        language,
        slugify(festival, slugOptions),
        path,
        slugify(title, slugOptions)
      ].filter(_ => _).join('/')
    });

    return entry;
  }

  static process (entry, language) {
    if (!entry || !entry.fields) return {};
    
    var _entry = entry.fields;
    _entry.id = entry.sys.id;
    if (entry.sys.contentType) _entry.type = entry.sys.contentType.sys.id;
    _entry = resolveLanguage(_entry, language);
    if (!_entry.language) _entry.language = language;
    if (_entry.relatedEvents) _entry.relatedEvents = [];

    // Limit the festival depth
    if (_entry.festival && _entry.festival.fields && _entry.festival.fields.navigationItems) {
      delete _entry.festival.fields.navigationItems;
    }
    
    // Recursive
    for (var property in _entry) {
      if (_entry[property] && _entry[property].sys) {
        _entry[property] = ContentfulEngine.process(_entry[property], language);
      }

      if (_entry[property] && Array.isArray(_entry[property]) && _entry[property].length > 0 && _entry[property][0] && _entry[property][0].sys && _entry[property][0].fields) {
        _entry[property] = _entry[property].map(_ => ContentfulEngine.process(_, language));
      }
    }

    _entry = ContentfulEngine.makeSlugs(_entry, language);
    _entry = resolveLanguage(_entry, language);

    if (typeof window == 'undefined') {
      _entry = clean(_entry, language);
      _entry = cleanUnusedFields(_entry, language);
    }
    
    return _entry;
  }

  getAll (type, language) {
    var _items = [];
    return new Promise((resolve, reject) => {
      this.client.getEntries({ 'content_type': type, include: 10, limit: 1000 })
        .then(response => {
          _items.push(...response.items.map(entry => ContentfulEngine.process(entry, language)));
          resolve(_items);
        })
        .catch(reject);
    });
  }

  getSingle (type, id, language) {
    return new Promise((resolve, reject) => {
      this.client.getEntries({ 'content_type': type, 'sys.id': id, include: 8 })
        .then(response => resolve(response.items.map(entry => ContentfulEngine.process(entry, language))))
        .catch(reject);
    });
  }

  getById (id, language) {
    return new Promise((resolve, reject) => {
      this.client.getEntries({ 'sys.id': id, include: 8 })
        .then(response => resolve(response.items.map(entry => ContentfulEngine.process(entry, language))))
        .catch(reject);
    });
  }

  getFirst (type, language) {
    return new Promise((resolve, reject) => {
      this.client.getEntries({ 'content_type': type, include: 8 })
        .then(response => {
          resolve(ContentfulEngine.process(response.items[0], language));
        })
        .catch(reject);
    });
  }

  search (term, types, language) {
    return new Promise((resolve, reject) => {
      this.client.getEntries({ 'content_type': types, 'query': term, include: 8 })
        .then(response => resolve(response.items.map(entry => ContentfulEngine.process(entry, language))))
        .catch(reject);
    });
  }
}

const resolveLanguage = (input, language) => {
  Object.keys(input).forEach(key => {
    if (key.substring(0, 2) === language) {
      var _ = key.substring(2);
      _ = _.charAt(0).toLowerCase() + _.slice(1);
      input[_] = input[key];
    }
  })
  return input;
}

const cleanCyclical = (data) => {
  const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return;
        }
        seen.add(value);
      }
      return value;
    };
  };
  
  return JSON.parse(JSON.stringify(data, getCircularReplacer()));
}

const cleanUnusedFields = (entry) => {
  if (entry.nlContentLeft) delete entry.nlContentLeft;
  if (entry.nlContentRight) delete entry.nlContentRight;
  if (entry.frContentLeft) delete entry.frContentLeft;
  if (entry.frContentRight) delete entry.frContentRight;
  if (entry.enContentLeft) delete entry.enContentLeft;
  if (entry.enContentRight) delete entry.enContentRight;
  if (entry.frExcerpt) delete entry.frExcerpt;
  if (entry.enExcerpt) delete entry.enExcerpt;
  if (entry.nlExcerpt) delete entry.nlExcerpt;
  if (entry.frIntro) delete entry.frIntro;
  if (entry.enIntro) delete entry.enIntro;
  if (entry.nlIntro) delete entry.nlIntro;
  if (entry.nlLongTitle) delete entry.nlLongTitle;
  if (entry.frLongTitle) delete entry.frLongTitle;
  if (entry.enLongTitle) delete entry.enLongTitle;
  
  return entry;
}

const clean = (entry, language) => {
  var _entry;
  try {
    _entry = JSON.parse(JSON.stringify(entry));
  } catch (e) {
    _entry = cleanCyclical(entry);
    _entry = JSON.parse(JSON.stringify(_entry));;
  }
  
  return _entry;
}

module.exports = ContentfulEngine;
